import Vue from 'vue'
import Router from 'vue-router'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { auth } from './firebaseConfig'

Vue.use(Router)

/**
 * Everywhere a schedule-only (station-only) member is allowed to go.
 *
 * An ALLOWLIST, not a blocklist, and that choice is load-bearing: Vue 2 has a
 * single global guard that today blocks four destinations, so every one of the
 * ~50 other routes — all fifteen `/staff/*` children, `/playground`,
 * `/customers/:email`, `/organizations/:uuid` — is reachable by default, and what
 * renders is decided purely by Firestore rules, which leave projects, chats,
 * users, accounts and organizations world-readable. A blocklist would fail OPEN
 * for every route added after this one.
 *
 * `404` is included so a bad link lands on the not-found page instead of
 * bouncing, which would otherwise read as a redirect loop.
 */
export const SCHEDULE_ONLY_ALLOWED_ROUTES = ['Schedule', 'Account', 'Login', '404']

if (Capacitor.isNativePlatform()) {
  App.addListener('appUrlOpen', function (data) {
    const slug = data.url.split('.com').pop()
    if (slug) {
      router.push({
        path: slug,
      })
    }
  })
}

let pendingChatOpen = null
let pendingReaderOpen = null
let pendingReaderElementOpen = null

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/components/Login'),
    },
    {
      path: '/404',
      name: '404',
      component: () => import('@/components/404Page'),
    },
    {
      path: '/',
      name: 'TaskPageHome',
      component: () => import('@/components/extras/TaskPage'),
      props: { path: 'formfactories' },
    },
    {
      path: '/dev',
      name: 'DevDashboard',
      component: () => import('@/components/DevDashboard'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/support',
      name: 'StaffSupport',
      component: () => import('@/components/StaffSupportPage'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/notifications',
      name: 'NotificationsDashboard',
      component: () => import('@/components/extras/NotificationsDashboard'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      // Phone alerts live only in the Vue 3 app. This route exists so `/alerts` (the page a
      // phone pins to its Home Screen) reaches the hand-off in main.js instead of falling into
      // `/:path` below; see NEW_APP_ONLY_ROUTES in utils/vue3DefaultOn.js.
      path: '/alerts',
      name: 'Alerts',
      component: () => import('@/components/extras/AlertsHandoff'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/dashboard',
      name: 'DashboardDefaultPage',
      component: () => import('@/components/DashboardPage'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/dashboard/:view',
      name: 'DashboardPage',
      component: () => import('@/components/DashboardPage'),
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/chat/:uuid',
      name: 'Chat',
    },
    {
      path: '/scanner',
      name: 'Scanner',
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/reader/:uuid',
      name: 'Reader',
    },
    {
      path: '/wiki/:task',
      name: 'Wiki',
      component: () => import('@/components/extras/TaskReader'),
      props: (route) => ({ navigationTask: route.params.task, fullscreen: true }),
    },
    {
      path: '/wiki',
      name: 'WikiHome',
      component: () => import('@/components/extras/TaskReader'),
      props: (route) => ({ fullscreen: true }),
    },
    {
      path: '/docs/:task',
      name: 'Docs',
      component: () => import('@/components/extras/TaskReader'),
      props: (route) => ({ navigationTask: route.params.task, fullscreen: true }),
    },
    {
      path: '/docs',
      name: 'DocsHome',
      component: () => import('@/components/extras/TaskReader'),
      props: (route) => ({ fullscreen: true }),
    },
    {
      path: '/reader/:task/:element',
      name: 'ReaderElement',
    },
    {
      path: '/projects',
      name: 'MyProjectsPage',
      component: () => import('@/components/MyProjectsPage'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/projects/:uuid/:view?',
      name: 'ProjectPage',
      component: () => import('@/components/project/ProjectPage'),
      props: true,
    },
    {
      path: '/projects/:projectUuid/models/:modelUuid',
      name: 'ModelViewPage',
      component: () => import('@/components/model/ModelViewPage'),
      props: true,
    },
    {
      path: '/analytics',
      name: 'AnalyticsPage',
      component: () => import('@/components/metrics/AnalyticsRouting'),
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          name: 'Business Analytics',
          path: 'business',
          component: () => import('@/components/metrics/business/BusinessAnalytics.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'Production Analytics',
          path: 'production',
          component: () => import('@/components/metrics/ProductionAnalytics'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'Machine Analytics',
          path: 'machines',
          component: () => import('@/components/metrics/MachinesAnalytics'),
          meta: {
            requiresAuth: true,
          },
        },
      ]
    },
    {
      path: '/salesAnalytics',
      name: 'SalesAnalytics',
      component: () => import('@/components/metrics/SalesAnalytics'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/task-draft-preview/:path',
      name: 'TaskDraftPreview',
      component: () => import('@/components/extras/TaskPage'),
      props: (route) => ({ path: route.params.path, previewMode: true }),
    },
    {
      path: '/info/:path',
      name: 'TaskPageOld',
      component: () => import('@/components/extras/TaskPage'),
      props: true,
    },
    {
      path: '/materials/:name',
      name: 'MaterialPage',
      component: () => import('@/components/extras/MaterialPage'),
      props: true,
    },
    {
      path: '/products',
      name: 'AllProducts',
      component: () => import('@/components/extras/ProductSquarePage'),
    },
    {
      path: '/products/:category',
      name: 'CategoryProducts',
      component: () => import('@/components/extras/ProductSquarePage'),
      props: true,
    },
    {
      path: '/products/:category/:path',
      name: 'ProductPage',
      component: () => import('@/components/extras/ProductComponent'),
      props: true,
    },
    {
      path: '/products/:category/:path/:sku',
      name: 'ProductVariantPage',
      component: () => import('@/components/extras/ProductComponent'),
      props: true,
    },
    {
      path: '/submit',
      name: 'Submit Files',
      component: () => import('@/components/SubmitPage'),
    },
    {
      path: '/counting',
      name: 'Counting',
      component: () => import('@/components/extras/Counting'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/collection',
      name: 'DesignCollectionHome',
      component: () => import('@/components/extras/DesignCollectionInterface'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/collection/:id',
      name: 'DesignCollection',
      component: () => import('@/components/extras/DesignCollectionInterface'),
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/design/:id',
      name: 'Design',
      component: () => import('@/components/extras/Design'),
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'Workspaces',
      path: '/workspaces',
      component: () => import('@/components/extras/Tasks'),
      props: { kanban: true },
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'TimeTracker',
      path: '/time-tracker',
      component: () => import('@/components/extras/TimeTrackerWindow'),
      meta: {
        requiresAuth: true,
        hideHeader: true,
      },
    },
    {
      name: 'Schedule',
      path: '/schedule',
      component: () => import('@/components/schedule/SchedulePage'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'Machines',
      path: '/machines',
      component: () => import('@/components/machines/MachineDashboard'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/playground',
      name: 'Playground',
      component: () => import('@/components/extras/Playground'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/landing',
      name: 'Landing',
      component: () => import('@/components/extras/Landing'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/inventory',
      name: 'Inventory',
      component: () => import('@/components/extras/Inventory'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/inventory/:id',
      name: 'Inventory Item',
      component: () => import('@/components/extras/Inventory'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/queue',
      name: 'Print Queue',
      component: () => import('@/components/extras/PrintQueue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'Clients',
      path: '/clients',
      component: () => import('@/components/staff/CustomerList'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/customers/:email',
      name: 'User',
      component: () => import('@/components/staff/User'),
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/company/:company',
      name: 'Company',
      component: () => import('@/components/staff/Company'),
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/organizations/:uuid',
      name: 'Organization',
      component: () => import('@/components/staff/Organization'),
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'Account',
      path: '/account',
      component: () => import('@/components/extras/Account'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'EnterpriseSignup',
      path: '/get-started',
      component: () => import('@/components/staff/EnterpriseSignup'),
    },
    {
      name: 'Subscriptions',
      path: '/subscriptions/:id',
      component: () => import('@/components/staff/CheckoutSession'),
      props: true,
    },
    {
      path: '/staff',
      component: () => import('@/components/staff/Staff'),
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          name: 'Dashboard',
          path: 'dashboard',
          component: () => import('@/components/staff/StaffDashboard'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'catalog',
          name: 'CatalogPage',
          component: () => import('@/components/staff/Catalog'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'products',
          name: 'ProductsCatalog',
          component: () => import('@/components/staff/Products'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'Media',
          path: 'media',
          component: () => import('@/components/extras/Media'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'pricing',
          name: 'PricingPage',
          component: () => import('@/components/staff/Pricing'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'emails',
          name: 'EmailsPage',
          component: () => import('@/components/staff/Emails'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'machines',
          name: 'MachinesPane',
          component: () => import('@/components/staff/Machines'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'integrations',
          name: 'Integrations',
          component: () => import('@/components/staff/Integrations'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'enterprises',
          name: 'Enterprises',
          component: () => import('@/components/staff/Enterprises'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'contact-suggestions',
          name: 'Contact Suggestions',
          component: () => import('@/components/staff/ContactSuggestions'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'features',
          name: 'Features',
          component: () => import('@/components/staff/Features'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'general',
          name: 'General',
          component: () => import('@/components/staff/Features'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'billing',
          name: 'Billing',
          component: () => import('@/components/staff/Billing'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'agents',
          name: 'Formfactories Agents',
          component: () => import('@/components/staff/Agents.vue'),
          meta: {
            requiresAuth: true,
          }
        },
        {
          path: 'server-status',
          name: 'Server Status',
          component: () => import('@/components/staff/ServerStatus'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'roles',
          name: 'Roles',
          component: () => import('@/components/staff/Roles'),
          meta: {
            requiresAuth: true,
          },
        },
      ],
    },
    {
      path: '/sitemaptest',
      name: 'SitemapTest',
      meta: {
        externalRedirect: `https://api.incept3d.com/meta/${encodeURIComponent(
          window.location.origin
        )}/sitemap.xml`,
      },
    },
    {
      path: '/:path',
      name: 'TaskPage',
      component: () => import('@/components/extras/TaskPage'),
      props: true,
    },
    {
      path: '*',
      redirect: '/404',
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (to.name.startsWith('Dashboard'))
      document.getElementById('router-content').scroll({ top: 0 })
  },
})

router.beforeEach((to, from, next) => {
  // Redirect externally if needed
  if (to.meta.externalRedirect) {
    const url = to.meta.externalRedirect
    window.location.replace(url)
    return
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const currentUser = auth.currentUser || window.currentUser
  // 'task' (Workspace homepage) is deprecated and no longer selectable; unset enterprises default to the Submit Files page
  const homepageDestination =
    (router.app.enterprise || window.enterprise.enterprise).homepageDestination || 'submit'

  let isStaff = false
  const loggingOut = !!(router.app && router.app.loggingOut)
  if (router.app && router.app.isStaff !== undefined) isStaff = !!router.app.isStaff
  else if (window.userToken) {
    const legacyClaims = (window.userToken || {}).claims || {}
    const enterpriseClaims = (legacyClaims.enterprises || {})[window.enterpriseID]
    const claims = enterpriseClaims || legacyClaims
    isStaff = claims.access >= 5
  }

  const inviteCode = new URLSearchParams(window.location.search).get('invite')
  if (inviteCode && router.app && to.name !== 'Login' && isStaff) {
    router.app.logout()
    return next(from)
  }

  // Schedule-only containment. Runs BEFORE the schedule-enterprise bounce below,
  // because that bounce sends you to the dashboard and containment would send you
  // straight back — an infinite loop for a schedule-only member pinned to an
  // enterprise where the scheduler is not enabled. Such a member has nowhere
  // legitimate to be, so they get signed out with an explanation rather than
  // volleyed between two guards.
  //
  // `scheduleOnlyPending` is respected: the membership read is a Firestore
  // listener with no token to fall back on, so acting before it resolves would
  // bounce a real member on every hard refresh. The same goes for the Beta
  // setting: until config/public loads, "off" is unknown, so nobody is signed
  // out for it here (main.js's scheduleEnabled watcher catches a late "off").
  const app = router.app
  if (app && app.isScheduleOnly && !app.scheduleOnlyPending) {
    const scheduleEnabledHere = !app.configPublicLoaded || app.scheduleEnabled
    if (!scheduleEnabledHere) {
      if (to.name === 'Login') return next()
      // Logout OWNS the Login navigation (and carries the explanation query);
      // issuing our own next(Login) here as well would race two navigations
      // and whichever lost would drop the query.
      if (app.logout) {
        app.logout({ noScheduleAccess: '1' })
        return next(false)
      }
      return next({ name: 'Login', query: { noScheduleAccess: '1' } })
    }
    if (!SCHEDULE_ONLY_ALLOWED_ROUTES.includes(to.name)) {
      return next({ name: 'Schedule' })
    }
  }

  // Before config/public loads the page renders its own loading placeholder, so
  // a hard refresh on /schedule is not bounced for an enterprise that has the
  // Beta setting on but is not on the legacy allowlist.
  if (to.name === 'Schedule' && app && app.configPublicLoaded && !app.scheduleEnabled) {
    return next({ name: 'DashboardDefaultPage' })
  }

  // The Notification Center is a staff/dev triage surface (isStaff already includes devs). Keep
  // non-staff (customers) out entirely so we don't have to support notification-center behavior for
  // that audience. Guarded on currentUser so an unauthenticated hit still falls through to the auth
  // check below and lands on Login rather than here.
  if (to.name === 'NotificationsDashboard' && currentUser && !isStaff) {
    return next({ name: 'DashboardDefaultPage' })
  }

  // The whole /staff tree is the staff workspace, but its children gate only on
  // requiresAuth - so any signed-in customer could open them and read whatever
  // the world-readable collections render. A PATH-PREFIX gate rather than
  // per-route meta, so a /staff child added later is protected by default
  // instead of reachable by omission. Guarded on currentUser (an
  // unauthenticated hit still falls through to the Login redirect below), and
  // exempt while staff access is still resolving - bouncing on that window
  // would kick a real staff member off every hard refresh; a non-staff visitor
  // who slips through it is evicted by the staffAccessPending watcher
  // (main.js) the moment their access resolves.
  if (
    to.path.startsWith('/staff') &&
    currentUser &&
    !isStaff &&
    !(router.app && router.app.staffAccessPending)
  ) {
    return next({ name: 'DashboardDefaultPage' })
  }

  if (
    requiresAuth &&
    !currentUser &&
    to.name !== 'Login' &&
    !(to.name === 'Home' || to.name === 'DashboardDefaultPage' || to.name === 'DashboardPage')
  ) {
    return next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if (to.name === 'TaskPageHome' && isStaff) {
    return next({ name: 'DashboardDefaultPage' })
  } else {
    if (to.name === 'TaskPageHome' && !isStaff && homepageDestination !== 'task') {
      if (homepageDestination === 'login') next({ name: 'Login' })
      else if (homepageDestination === 'submit') next({ name: 'Submit Files' })
      else if (homepageDestination === 'formfactories-signup') next({ name: 'EnterpriseSignup' })
      else return next()
    } else if (to.name === 'Chat') {
      next(from)
      if (router.app.openChat) router.app.openChat(to.params.uuid)
      else pendingChatOpen = to.params.uuid
    } else if (to.name === 'Scanner') {
      next(from)
      if (router.app.navigation) router.app.navigation.qrScannerOpen = true
      else window.openScanner = true
    } else if (to.name === 'Reader') {
      next(from)
      if (router.app.openReader) router.app.openReader(to.params.uuid)
      else pendingReaderOpen = to.params.uuid
    } else if (to.name === 'ReaderElement') {
      next(from)
      if (router.app.openReader) router.app.openReader(to.params.task, to.params.element)
      else {
        pendingReaderOpen = to.params.task
        pendingReaderElementOpen = to.params.element
      }
    } else {
      if (to.name === 'Login' && currentUser && isStaff && !inviteCode && !loggingOut) return next('/')
      else if (to.name === 'Login' && currentUser && !isStaff && !inviteCode && !loggingOut) return next('/')
      else if (
        (to.name === 'Home' || to.name === 'DashboardDefaultPage' || to.name === 'DashboardPage') &&
        !isStaff
      )
        return next('/')
      else return next()
    }
  }
})

// Silence non-fatal navigation redirections in dev overlay
router.onError((err) => {
  if (err && typeof err.message === 'string' && err.message.includes('Redirected when going from')) {
    return
  }
  // For other errors, allow default handling
})

router.afterEach((to, from) => {
  if (pendingChatOpen && router.app.openChat) {
    router.app.openChat(pendingChatOpen)
    pendingChatOpen = null
  }

  if (pendingReaderOpen && router.app.openReader) {
    router.app.openReader(pendingReaderOpen, pendingReaderElementOpen)
    pendingReaderOpen = null
    pendingReaderElementOpen = null
  }
})

export default router

